import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";
import { api } from "@/lib/api";
import { useTheme } from "@/context/ThemeContext";
import { fmtEur, YEARS } from "@/lib/format";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const Delta = ({ a, b, testId }) => {
  if (!a && !b) return <span className="text-xs text-muted-foreground">—</span>;
  const pct = a ? ((b - a) / a) * 100 : 100;
  const Icon = pct > 0.05 ? ArrowUpRight : pct < -0.05 ? ArrowDownRight : Minus;
  const cls = pct > 0.05 ? "text-red-600 dark:text-red-400" : pct < -0.05 ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground";
  return <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${cls}`} data-testid={testId}><Icon className="h-3.5 w-3.5" />{pct > 0 ? "+" : ""}{pct.toLocaleString("fr-FR", { maximumFractionDigits: 1 })} %</span>;
};

export const ExerciseCompare = ({ companyId, year }) => {
  const { isDark } = useTheme();
  const [other, setOther] = useState(YEARS.includes(year + 1) ? year + 1 : year - 1);
  const [data, setData] = useState(null);

  useEffect(() => { setOther(YEARS.includes(year + 1) ? year + 1 : year - 1); }, [year]);
  useEffect(() => {
    if (!companyId || other === year) return;
    setData(null);
    api.get(`/companies/${companyId}/comparatif`, { params: { a: year, b: other } }).then((r) => setData(r.data)).catch(() => {});
  }, [companyId, year, other]);

  const chart = data ? data.a.mensuel.map((m, i) => ({ label: m.label, [data.a.annee]: m.taxe_brute, [data.b.annee]: data.b.mensuel[i].taxe_brute })) : [];
  const rows = data ? [
    ["Effectif", data.a.effectif, data.b.effectif, (v) => v, "cmp-effectif"],
    ["Masse salariale", data.a.masse_salariale, data.b.masse_salariale, fmtEur, "cmp-payroll"],
    ["Taxe brute", data.a.taxe_brute, data.b.taxe_brute, fmtEur, "cmp-tax-gross"],
    ["Taxe nette", data.a.taxe_nette, data.b.taxe_nette, fmtEur, "cmp-tax-net"],
  ] : [];

  return (
    <div className="bg-card border border-border rounded-md p-5" data-testid="exercise-compare">
      <div className="flex items-center justify-between mb-4">
        <div className="eyebrow">Comparatif d'exercices · {year} vs {other}</div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">Comparer avec
          <Select value={String(other)} onValueChange={(v) => setOther(Number(v))}>
            <SelectTrigger className="h-8 w-24 font-mono" data-testid="compare-year-select"><SelectValue /></SelectTrigger>
            <SelectContent>{YEARS.filter((y) => y !== year).map((y) => <SelectItem key={y} value={String(y)} data-testid={`compare-year-${y}`}>{y}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      {!data ? <div className="text-sm text-muted-foreground">Chargement…</div> : (
        <div className="grid lg:grid-cols-[1fr_360px] gap-6">
          <div className="h-60 min-h-[240px] w-full" data-testid="compare-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chart} margin={{ left: 0, right: 0, top: 4, bottom: 0 }} barGap={2}>
                <CartesianGrid vertical={false} stroke={isDark ? "#2A2650" : "#E2E8F0"} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: isDark ? "#9A95B8" : "#64748B" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: isDark ? "#9A95B8" : "#64748B" }} axisLine={false} tickLine={false} width={64} tickFormatter={(v) => `${Math.round(v)} €`} />
                <Tooltip formatter={(v) => fmtEur(v)} cursor={{ fill: isDark ? "#201C42" : "#F1F5F9" }} contentStyle={isDark ? { background: "#171433", border: "1px solid #2A2650", color: "#F1EFFA" } : undefined} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey={String(data.a.annee)} fill="#7161D0" radius={[3, 3, 0, 0]} />
                <Bar dataKey={String(data.b.annee)} fill={isDark ? "#C0B8E8" : "#141226"} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <table className="text-sm w-full self-start" data-testid="compare-table">
            <thead><tr className="text-xs text-muted-foreground"><th className="text-left font-medium pb-2">Indicateur</th><th className="text-right font-mono pb-2">{data.a.annee}</th><th className="text-right font-mono pb-2">{data.b.annee}</th><th className="text-right pb-2">Δ</th></tr></thead>
            <tbody>
              {rows.map(([label, a, b, f, id]) => (
                <tr key={label} className="border-t border-border/60">
                  <td className="py-2 text-foreground/80">{label}</td>
                  <td className="py-2 text-right font-mono whitespace-nowrap">{f(a)}</td>
                  <td className="py-2 text-right font-mono font-semibold whitespace-nowrap pl-2" data-testid={`${id}-b`}>{f(b)}</td>
                  <td className="py-2 text-right whitespace-nowrap pl-2"><Delta a={a} b={b} testId={`${id}-delta`} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
